import { useAuthStore } from '@/store/pinia/auth'
import { useCustomersStore } from '@/store/pinia/customers'
import { useRootStore } from '@/store/pinia/root'

const assignOwner = {
  readMembers: 'GetOrgMembers',
  readOwner: 'GetOrgMember',
}

// The three CCM read permissions must always be evaluated together. The overview pages gate their
// cost/carbon panels on `isDenied`, which only returns a verdict for actions warmed on the current
// route — so `GetCloudCostManagement` (canSeeCCM) and the two panel actions have to be co-located in
// every node that reads CCM. Spreading one shared object keeps them from drifting apart: evaluating
// the parent action without the two panel actions would make those panels silently fail open.
const ccmReadActions = {
  readCCM: 'GetCloudCostManagement',
  readCCMCost: 'GetCloudCostManagementCost',
  readCCMCarbonFootprint: 'GetCloudCostManagementCarbonFootprint',
}

/**
 * @typedef {import('@/assets/policies/actions.json')} mainActions
 * @typedef {import('@/assets/policies/alpha-actions.json')} alphaActions
 *
 * @typedef {keyof (mainActions & alphaActions)} ActionKey
 * @typedef {ActionKey[] | (() => ActionKey[])} ActionsEnumLeaf
 *
 * @typedef {{ requiredActions?: ActionsEnumLeaf, actions?: Record<string, ActionKey> }} ActionsNode
 *
 * @typedef {ActionsNode & { [key: string]: ActionsNode | ResourceNode }} ResourceNode
 * @type {ResourceNode}
 */

export const actionsWithResources = {
  organization: {
    dashboard: {
      requiredActions: () => {
        const customersStore = useCustomersStore()
        return customersStore.isInCustomerScope
          ? ['GetChildren']
          : ['GetOrgs']
      },
      actions: {
        ...ccmReadActions,
        readCredentials: 'ListCredentials',
        readEvents: 'GetEvents',
        readKPIs: 'GetKPIs',
        readPipelines: 'GetPipelines',
        readProjects: 'GetProjects',
        readStacks: 'GetServiceCatalogSources',
      },
    },
    settings: {
      index: {
        requiredActions: ['GetOrg', 'GetOrgSettings'],
        actions: {
          activateLicence: 'ActivateLicence',
          create: 'CreateOrg',
          createAppearance: 'CreateAppearance',
          createCloudAccount: 'CreateCloudAccount',
          createEnvironment: 'CreateEnvironment',
          createOidcGroupMapping: 'CreateOIDCGroupMapping',
          deleteOidcGroupMapping: 'DeleteOIDCGroupMapping',
          listOidcGroupMappings: 'ListOIDCGroupMappings',
          readOidcSettings: 'GetOIDCSettings',
          updateOidcSettings: 'UpdateOIDCSettings',
          delete: 'DeleteOrg',
          read: 'GetOrg',
          readAppearances: 'ListAppearances',
          readAuthentications: 'ListAuthentications',
          readChildOrgs: 'GetChildren',
          readCloudAccounts: 'GetCloudAccounts',
          readEnvironments: 'GetEnvironments',
          readExternalBackends: 'GetExternalBackends',
          readLicence: 'GetLicence',
          readMembers: 'GetOrgMembers',
          readTeams: 'GetTeams',
          // The Teams tab is rendered under this route (?section=teams), so its team
          // write gates must be evaluated here too. Without them a non-admin custom role
          // holding the permission reads them as never-evaluated — i.e. fail closed — and
          // the Create/Delete/Reassign-owner controls stay hidden (ENG-453).
          createTeam: 'CreateTeam',
          deleteTeam: 'DeleteTeam',
          updateTeam: 'UpdateTeam',
          readWorkers: 'GetWorkers',
          createWorker: 'CreateWorker',
          update: 'UpdateOrg',
          updateAppearance: 'UpdateAppearance',
        },
      },
      appearance: {
        create: {
          requiredActions: ['CreateAppearance', 'GetOrgSettings'],
          actions: {
            create: 'CreateAppearance',
            delete: 'DeleteAppearance',
            read: 'GetAppearance',
            update: 'UpdateAppearance',
          },
        },
        details: {
          requiredActions: ['GetAppearance', 'GetOrgSettings'],
          actions: {
            create: 'CreateAppearance',
            delete: 'DeleteAppearance',
            read: 'GetAppearance',
            readList: 'ListAppearances',
            update: 'UpdateAppearance',
          },
        },
      },
      terraform: {
        requiredActions: ['GetExternalBackends', 'GetOrgSettings'],
        actions: {
          create: 'CreateExternalBackend',
          update: 'UpdateExternalBackend',
        },
      },
      plugins: {
        details: {
          requiredActions: ['GetPluginRegistryPlugin', 'GetOrgSettings'],
          actions: {
            read: 'GetPluginRegistryPlugin',
            readVersions: 'ListPluginRegistryPluginVersions',
            create: 'CreatePluginRegistryPluginVersion',
            delete: 'DeletePluginRegistryPluginVersion',
          },
        },
        versions: {
          requiredActions: ['ListPluginRegistryPluginVersions', 'GetOrgSettings'],
          actions: {
            create: 'CreatePluginRegistryPluginVersion',
            delete: 'DeletePluginRegistryPluginVersion',
            retry: 'RetryPluginRegistryPluginVersion',
            readLogs: 'ListPluginRegistryPluginVersionLogs',
          },
        },
        widgets: {
          requiredActions: ['GetOrgSettings'],
        },
      },
    },
    workers: {
      requiredActions: ['GetOrgSettings'],
      actions: {
        read: 'GetWorkers',
        create: 'CreateWorker',
      },
    },
  },
  organizations: {
    index: {
      requiredActions: ['GetOrgs'],
      actions: {
        createOrg: 'CreateOrg',
        createOrgChild: 'CreateChild',
      },
    },
  },
  customers: {
    actions: {
      read: 'GetChildren',
    },
  },
  inventory: {
    index: {
      requiredActions: ['ListInventoryResources'],
    },
  },
  projects: {
    index: {
      requiredActions: ['GetProjects'],
      actions: {
        ...assignOwner,
        create: 'CreateProject',
        delete: 'DeleteProject',
        read: 'GetProject',
        readPipeline: 'GetPipeline',
        update: 'UpdateProject',
      },
    },
  },
  project: {
    details: {
      requiredActions: ['GetProject'],
      actions: {
        ...assignOwner,
        create: 'CreateProject',
        createEnvironment: 'CreateEnvironment',
        delete: 'DeleteProject',
        listConfigRepos: 'ListConfigRepositories',
        readConfig: 'GetProjectConfig',
        readConfigRepo: 'GetConfigRepository',
        readEnvironments: 'GetEnvironments',
        readInfra: 'GetProjInfrastructure',
        readLogs: 'GetProjEnvLogStreams',
        readMetrics: 'GetKPIs',
        readPipeline: 'GetPipeline',
        update: 'UpdateProject',
        readCR: 'GetServiceCatalogSource',
      },
    },
    metrics: {
      requiredActions: ['GetKPIs'],
      actions: {
        create: 'CreateKPI',
        update: 'UpdateKPI',
        delete: 'DeleteKPI',
      },
    },
    // Only consumed by the "add a component" route: a component is authorized on its own
    // CreateComponent action (BE-473), never on CreateProject.
    create: {
      requiredActions: ['CreateComponent'],
      actions: {
        create: 'CreateComponent',
        editStack: 'UpdateServiceCatalog',
        readCatalogRepos: 'GetServiceCatalogSources',
        listStacks: 'ListServiceCatalogs',
        useStack: 'GetServiceCatalog',
        // Warmed before StackForms renders: the cy_cred widget and the teams fetch gate on them,
        // and an unevaluated action locks the widget until it is evaluated (ENG-459)
        createCredential: 'CreateCredential',
        readTeams: 'GetTeams',
      },
    },
    environments: {
      update: {
        requiredActions: ['UpdateProject'],
        actions: {
          read: 'GetServiceCatalog',
          readKPIs: 'GetKPIs',
        },
      },
      index: {
        actions: {
          create: 'CreateBuild',
          createComponent: 'CreateComponent',
          createEnvironment: 'CreateEnvironment',
          // Component write actions (BE-473), evaluated with the component path automatically, so
          // the Settings / Change stack version / Delete menu items stay visible to a user who
          // holds component update/delete without being owner/admin. Unevaluated actions read as
          // false, which would otherwise hide them on the project and environment overview pages.
          updateComponent: 'UpdateComponent',
          deleteComponent: 'DeleteComponent',
          // Evaluated with the route's project and environment canonicals, so the answer is
          // scoped the way the backend scopes the listing itself (BE-473). Not a requiredAction:
          // the environment page stays reachable and says why the components are missing.
          listComponents: 'ListComponents',
          listStacks: 'ListServiceCatalogs',
        },
      },
    },
    pipelines: {
      index: {
        requiredActions: ['GetPipelines'],
        actions: {
          read: 'GetPipeline',
          readJob: 'GetJob',
          update: 'UpdatePipeline',
          readKPIs: 'GetKPIs',
        },
      },
      builds: {
        actions: {
          pause: 'PauseJob',
          unpause: 'UnpauseJob',
          abort: 'AbortBuild',
          create: 'CreateBuild',
          readAll: 'GetBuilds',
          read: 'GetBuild',
          readResource: 'GetBuildResources',
          reRunBuild: 'RerunBuild',
        },
      },
      resources: {
        actions: {
          update: 'UnpinResource',
          set: 'ResourceSetPinComment',
          read: 'GetResource',
          readVersion: 'GetResourceVersions',
        },
      },
    },
    // Shared by every configuration tab (general, environment, logs), so it has to warm the
    // actions all of them read. An action missing here is not "unknown" to canDisplay, it is
    // false: the general tab would offer no project edit and the environment tab would render
    // its name and description read-only, whatever the role actually holds.
    configuration: {
      actions: {
        ...assignOwner,
        createExternalBackend: 'CreateExternalBackend',
        deleteExternalBackend: 'DeleteExternalBackend',
        deleteProject: 'DeleteProject',
        listConfigRepositories: 'ListConfigRepositories',
        listCredentials: 'ListCredentials',
        readCloudAccount: 'GetCloudAccount',
        readCloudProvider: 'GetCloudProviders',
        readCredentials: 'GetCredential',
        readExternalBackends: 'GetExternalBackends',
        readKPIs: 'GetKPIs',
        updateEnvironment: 'UpdateEnvironment',
        updateExternalBackend: 'UpdateExternalBackend',
        updateProject: 'UpdateProject',
      },
    },
    logs: {
      index: {
        requiredActions: ['GetProjEnvLogStreams'],
        actions: {
          read: 'GetProjEnvLogStreams',
          readKPIs: 'GetKPIs',
        },
      },
      elasticsearch: {
        create: {
          requiredActions: ['CreateExternalBackend'],
          actions: {
            create: 'CreateExternalBackend',
            read: 'GetExternalBackends',
            readCloudProvider: 'GetCloudProviders',
            listConfigRepositories: 'ListConfigRepositories',
          },
        },
        details: {
          requiredActions: ['GetExternalBackend'],
          actions: {
            delete: 'DeleteExternalBackend',
            read: 'GetExternalBackends',
            readCloudProvider: 'GetCloudProviders',
            listConfigRepositories: 'ListConfigRepositories',
            update: 'UpdateExternalBackend',
          },
        },
      },
    },
    infraview: {
      index: {
        requiredActions: () => {
          const rootStore = useRootStore()
          const authStore = useAuthStore()

          // Allow access if user is org admin
          if (!rootStore.hasOrgs || rootStore.isOrgAdmin) return []

          // Allow access if user has either ListInventoryOutputs OR GetExternalBackend (read permission)
          const hasListInventoryOutputs = authStore.evaluatedActions?.ListInventoryOutputs?.ok ?? false
          const hasGetExternalBackend = authStore.evaluatedActions?.GetExternalBackend?.ok ?? false

          if (hasListInventoryOutputs || hasGetExternalBackend) {
            return [] // Grant access
          }

          // Deny access - require ListInventoryOutputs as the primary permission
          return ['ListInventoryOutputs']
        },
        actions: {
          read: 'GetProjInfrastructure',
          update: 'UpdateExternalBackend',
          readKPIs: 'GetKPIs',
          create: 'CreateExternalBackend',
          // Load-bearing: both entries are always included in the EVALUATE_USER_ACTIONS fetch
          // batch (via Object.values(actions)) so requiredActions() always reads a warm cache,
          // and revoked permissions are caught immediately on the next navigation rather than
          // waiting for the 2-minute TTL to expire.
          readExternalBackend: 'GetExternalBackend',
          listInventoryOutputs: 'ListInventoryOutputs',
        },
      },
      create: {
        requiredActions: ['CreateExternalBackend'],
        actions: {
          create: 'CreateExternalBackend',
        },
      },
      details: {
        requiredActions: ['GetExternalBackend'],
        actions: {
          read: 'GetProjInfrastructure',
          update: 'UpdateExternalBackend',
        },
      },
    },
  },
  // Component actions (BE-473) prefetched for the pages that show a single component: the
  // overview/configuration/settings pages and the plugin widget. No requiredActions here, same
  // as before the component policies existed - access is enforced by the data fetch itself, not
  // by the router guard.
  components: {
    details: {
      actions: {
        read: 'GetComponent',
        update: 'UpdateComponent',
        delete: 'DeleteComponent',
        // CyEnvComponentActions also renders in the component-page header, so Clone's
        // CreateComponent has to be warmed here too or Clone disappears for non-owners (BE-473).
        createComponent: 'CreateComponent',
        // Warmed for the StackForms cy_cred widget and teams fetch, see project.create (ENG-459)
        createCredential: 'CreateCredential',
        readTeams: 'GetTeams',
      },
    },
  },
  events: {
    index: {
      requiredActions: ['GetEvents'],
      actions: {
        read: 'GetEvents',
      },
    },
  },
  pipelines: {
    index: {
      requiredActions: ['GetPipelines'],
      actions: {
        read: 'GetPipeline',
        readJob: 'GetJob',
        update: 'UpdatePipeline',
      },
    },
  },
  catalogRepositories: {
    index: {
      requiredActions: ['GetServiceCatalogSources'],
      actions: {
        create: 'CreateServiceCatalogSource',
        delete: 'DeleteServiceCatalogSource',
        listCredential: 'ListCredentials',
        read: 'GetServiceCatalogSources',
        refresh: 'RefreshServiceCatalogSource',
        readCredential: 'GetCredential',
      },
    },
    create: {
      requiredActions: ['CreateServiceCatalogSource'],
      actions: {
        create: 'CreateServiceCatalogSource',
        listCredential: 'ListCredentials',
        readCredential: 'GetCredential',
      },
    },
    details: {
      requiredActions: ['GetServiceCatalogSource'],
      actions: {
        create: 'CreateServiceCatalogSource',
        delete: 'DeleteServiceCatalogSource',
        refresh: 'RefreshServiceCatalogSource',
        update: 'UpdateServiceCatalogSource',
      },
    },
  },
  configRepositories: {
    index: {
      requiredActions: ['ListConfigRepositories'],
      actions: {
        create: 'CreateConfigRepository',
        delete: 'DeleteConfigRepository',
        listCredential: 'ListCredentials',
        read: 'GetConfigRepository',
        readCredential: 'GetCredential',
      },
    },
    create: {
      requiredActions: ['CreateConfigRepository'],
      actions: {
        create: 'CreateConfigRepository',
        listCredential: 'ListCredentials',
        readCredential: 'GetCredential',
      },
    },
    details: {
      requiredActions: ['GetConfigRepository'],
      actions: {
        create: 'CreateConfigRepository',
        delete: 'DeleteConfigRepository',
        update: 'UpdateConfigRepository',
      },
    },
  },
  credentials: {
    index: {
      requiredActions: ['ListCredentials'],
      actions: {
        ...assignOwner,
        create: 'CreateCredential',
        delete: 'DeleteCredential',
        list: 'ListCredentials',
        read: 'GetCredential',
        readAll: 'GetCredential',
        update: 'UpdateCredential',
      },
    },
    create: {
      requiredActions: ['CreateCredential'],
      actions: {
        create: 'CreateCredential',
      },
    },
    details: {
      requiredActions: ['GetCredential'],
      actions: {
        ...assignOwner,
        delete: 'DeleteCredential',
        list: 'ListCredentials',
        read: 'GetCredential',
        update: 'UpdateCredential',
      },
    },
  },
  members: {
    index: {
      requiredActions: ['GetOrgMembers', 'GetOrgSettings'],
      actions: {
        create: 'InviteUserToOrgMember',
        delete: 'RemoveOrgMember',
        read: 'GetOrgMember',
        readPendingInvites: 'GetOrgMembers',
        readRole: 'GetRole',
        update: 'UpdateOrgMember',
      },
    },
    details: {
      requiredActions: ['GetOrgMember', 'GetOrgSettings'],
      actions: {
        delete: 'RemoveOrgMember',
        read: 'GetOrgMember',
        update: 'UpdateOrgMember',
      },
    },
    invite: {
      requiredActions: [],
      actions: {},
    },
  },
  roles: {
    index: {
      requiredActions: ['GetRoles'],
      actions: {
        create: 'CreateRole',
        delete: 'DeleteRole',
        read: 'GetRole',
      },
    },
    create: {
      requiredActions: ['CreateRole'],
      actions: {
        create: 'CreateRole',
      },
    },
    details: {
      requiredActions: ['GetRole'],
      actions: {
        create: 'CreateRole',
        delete: 'DeleteRole',
        read: 'GetRole',
        readPolicies: 'GetPolicies',
        update: 'UpdateRole',
      },
    },
  },
  apiKeys: {
    index: {
      requiredActions: ['GetAPIKeys'],
      actions: {
        ...assignOwner,
        create: 'CreateAPIKey',
        delete: 'DeleteAPIKey',
        update: 'UpdateAPIKey',
      },
    },
    create: {
      requiredActions: ['CreateAPIKey'],
      actions: {
        create: 'CreateAPIKey',
      },
    },
    details: {
      requiredActions: ['GetAPIKeys'],
      actions: {
        ...assignOwner,
        create: 'CreateAPIKey',
        delete: 'DeleteAPIKey',
        read: 'GetAPIKeys',
        update: 'UpdateAPIKey',
      },
    },
  },
  stacks: {
    index: {
      requiredActions: ['ListServiceCatalogs'],
      actions: {
        create: 'CreateServiceCatalog',
        createProject: 'CreateProject',
        edit: 'UpdateServiceCatalog',
        readCatalogRepos: 'GetServiceCatalogSources',
        readStack: 'GetServiceCatalog',
        readCR: 'GetServiceCatalogSource',
      },
    },
    create: {
      requiredActions: ['CreateServiceCatalog'],
      actions: {
        create: 'CreateServiceCatalog',
      },
    },
    details: {
      actions: {
        read: 'GetServiceCatalog',
        readCR: 'GetServiceCatalogSource',
        update: 'UpdateServiceCatalog',
        delete: 'DeleteServiceCatalog',
        create: 'CreateServiceCatalog',
        createProject: 'CreateProject',
      },
    },
    edit: {
      requiredActions: [
        'GetServiceCatalog',
        'UpdateServiceCatalog',
        'GetServiceCatalogConfig',
      ],
    },
    tools: {
      requiredActions: [
        'CreateProject',
        'UpdateProject',
      ],
    },
  },
  teams: {
    index: {
      requiredActions: ['GetTeams', 'GetOrgSettings'],
      actions: {
        ...assignOwner,
        create: 'CreateTeam',
        delete: 'DeleteTeam',
        read: 'GetTeam',
        update: 'UpdateTeam',
      },
    },
    create: {
      requiredActions: ['CreateTeam', 'GetOrgSettings'],
      actions: {
        create: 'CreateTeam',
      },
    },
    details: {
      requiredActions: ['GetTeam', 'GetOrgSettings'],
      actions: {
        ...assignOwner,
        addMembers: 'AssignMemberToTeam',
        delete: 'DeleteTeam',
        readTeamMember: 'GetTeamMember',
        readTeamMembers: 'GetTeamMembers',
        unassignTeamMember: 'UnassignMemberFromTeam',
        update: 'UpdateTeam',
      },
    },
  },
  user: {
    /** User routes do not need permission checks.
     *
     * At least for CRUD of their own profile/account,
     * they ALWAYS have permission to do those things.
     */
    actions: {},
  },
  infraPolicies: {
    index: {
      requiredActions: ['GetInfraPolicies'],
      actions: {
        ...assignOwner,
        create: 'CreateInfraPolicy',
        delete: 'DeleteInfraPolicy',
        update: 'UpdateInfraPolicy',
      },
    },
    create: {
      requiredActions: ['CreateInfraPolicy'],
      actions: {
        create: 'CreateInfraPolicy',
      },
    },
    details: {
      requiredActions: ['GetInfraPolicy'],
      actions: {
        ...assignOwner,
        create: 'CreateInfraPolicy',
        delete: 'DeleteInfraPolicy',
        read: 'GetInfraPolicy',
        update: 'UpdateInfraPolicy',
      },
    },
  },
  cloudCostManagement: {
    index: {
      requiredActions: ['GetCloudCostManagement'],
      actions: {
        ...ccmReadActions,
        create: 'GetCloudCostManagementAccounts',
        update: 'GetCloudCostManagementTagMapping',
      },
    },
    accounts: {
      list: {
        requiredActions: ['GetCloudCostManagementAccounts'],
      },
    },
    tagMapping: {
      requiredActions: ['GetCloudCostManagementTagMapping'],
      actions: {
        update: 'PutCloudCostManagementTagMapping',
      },
    },
  },
  cloudAccountSection: {
    requiredActions: ['GetCloudAccount'],
    actions: {
      read: 'GetCloudAccounts',
      readOne: 'GetCloudAccount',
      create: 'CreateCloudAccount',
      update: 'UpdateCloudAccount',
      delete: 'DeleteCloudAccount',
    },
  },
  cloudAccountSectionEnvironments: {
    requiredActions: ['GetCloudAccount'],
    actions: {
      read: 'GetCloudAccounts',
      readOne: 'GetCloudAccount',
    },
  },
  cloudAccountSectionSettings: {
    requiredActions: ['GetCloudAccount'],
    actions: {
      update: 'UpdateCloudAccount',
      delete: 'DeleteCloudAccount',
    },
  },
  orgEnvironmentSection: {
    requiredActions: ['GetEnvironments'],
    actions: {
      read: 'GetEnvironments',
      update: 'UpdateEnvironment',
    },
  },
  orgEnvironmentSectionOverview: {
    requiredActions: ['GetEnvironments'],
    actions: {
      read: 'GetEnvironments',
    },
  },
  orgEnvironmentSectionVariables: {
    requiredActions: ['GetEnvironments'],
    actions: {
      read: 'GetEnvironments',
      update: 'UpdateEnvironment',
    },
  },
  orgEnvironmentSectionSettings: {
    requiredActions: ['GetEnvironments'],
    actions: {
      read: 'GetCloudAccount',
      update: 'UpdateEnvironment',
      delete: 'DeleteEnvironment',
    },
  },
}
